import React, { useState, useCallback } from 'react';
import './ManageLogbooks.css';
import collegeConfig from '../../config/collegeConfig';
import {
  FaSearch,
  FaTrashAlt,
  FaBookMedical,
  FaCheckSquare,
  FaSquare,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaChevronLeft,
  FaTimes,
  FaEye,
  FaListAlt,
  FaStickyNote,
  FaChalkboard,
} from 'react-icons/fa';

const STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  under_review: 'Under Review',
  approved: 'Approved',
  rejected: 'Rejected',
  needs_revision: 'Needs Revision',
};

const STATUS_COLORS = {
  draft: '#95a5a6',
  submitted: '#3498db',
  under_review: '#f39c12',
  approved: '#27ae60',
  rejected: '#e74c3c',
  needs_revision: '#e67e22',
};

const RATING_LABELS = { B: 'Below Expectations', M: 'Meets Expectations', E: 'Exceeds Expectations' };
const ATTEMPT_LABELS = { F: 'First Attempt', R: 'Repeat', Re: 'Remedial' };
const DECISION_LABELS = { C: 'Completed', R: 'Repeat', Re: 'Remedial' };

const ManageLogbooks = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('student');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState('');

  const [selectedUser, setSelectedUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [courseMap, setCourseMap] = useState({}); // courseId → course object
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [entriesError, setEntriesError] = useState('');

  const [selectedIds, setSelectedIds] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteSuccess, setDeleteSuccess] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const [viewEntry, setViewEntry] = useState(null); // entry being viewed in modal

  const token = localStorage.getItem('token');

  // ── helpers ──────────────────────────────────────────────
  const getModule = (entry) => {
    const course = courseMap[entry.courseId?._id || entry.courseId];
    if (!course) return null;
    const mid = entry.moduleId?._id?.toString() || entry.moduleId?.toString();
    return course.modules?.find(m => m.moduleId?.toString() === mid) || null;
  };

  const getModuleName = (entry) => getModule(entry)?.name || '—';

  const formatDate = (d) =>
    d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  const userName = (u) =>
    u ? `${u.profile?.firstName || ''} ${u.profile?.lastName || ''}`.trim() || u.email : '';

  // ── fetch helpers ─────────────────────────────────────────
  const fetchCourses = useCallback(async (courseIds) => {
    const unique = [...new Set(courseIds.filter(Boolean))];
    if (!unique.length) return;
    const results = await Promise.allSettled(
      unique.map(id =>
        fetch(`${collegeConfig.api.baseURL}/courses/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }).then(r => r.json())
      )
    );
    const map = {};
    results.forEach((r, i) => {
      if (r.status === 'fulfilled' && r.value.success) {
        map[unique[i]] = r.value.course;
      }
    });
    setCourseMap(prev => ({ ...prev, ...map }));
  }, [token]);

  const handleSearch = useCallback(async (e) => {
    e.preventDefault();
    if (searchQuery.trim().length < 2) {
      setSearchError('Enter at least 2 characters to search');
      return;
    }
    setSearchError('');
    setSearchLoading(true);
    setSearchResults([]);
    try {
      const res = await fetch(
        `${collegeConfig.api.baseURL}/logbook/admin/users/search?q=${encodeURIComponent(searchQuery)}&role=${roleFilter}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        setSearchResults(data.users);
        if (!data.users.length) setSearchError('No users found');
      } else {
        setSearchError(data.message || 'Search failed');
      }
    } catch {
      setSearchError('Search failed. Please try again.');
    } finally {
      setSearchLoading(false);
    }
  }, [searchQuery, roleFilter, token]);

  const loadEntries = useCallback(async (user) => {
    setSelectedUser(user);
    setSelectedIds([]);
    setDeleteError('');
    setDeleteSuccess('');
    setEntriesError('');
    setEntriesLoading(true);
    setEntries([]);
    setCourseMap({});
    try {
      const param = user.role === 'teacher' ? `reviewerId=${user._id}` : `studentId=${user._id}`;
      const res = await fetch(
        `${collegeConfig.api.baseURL}/logbook/entries?${param}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.success) {
        const loaded = data.entries || [];
        setEntries(loaded);
        if (!loaded.length) {
          setEntriesError(
            user.role === 'teacher'
              ? 'No logbook entries reviewed by this teacher.'
              : 'No logbook entries found for this student.'
          );
        } else {
          // fetch unique courses so we can resolve module names + fields
          const ids = [...new Set(loaded.map(e => e.courseId?._id || e.courseId).filter(Boolean))];
          fetchCourses(ids);
        }
      } else {
        setEntriesError(data.message || 'Failed to load entries');
      }
    } catch {
      setEntriesError('Failed to load entries. Please try again.');
    } finally {
      setEntriesLoading(false);
    }
  }, [token, fetchCourses]);

  // ── selection ─────────────────────────────────────────────
  const toggleSelect = (id) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const toggleSelectAll = () =>
    setSelectedIds(selectedIds.length === entries.length ? [] : entries.map(e => e._id));

  // ── delete ────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    setDeleting(true);
    setDeleteError('');
    setDeleteSuccess('');
    try {
      const res = await fetch(`${collegeConfig.api.baseURL}/logbook/admin/entries/bulk`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ entryIds: selectedIds }),
      });
      const data = await res.json();
      if (data.success) {
        setDeleteSuccess(`Successfully deleted ${data.deleted} entr${data.deleted === 1 ? 'y' : 'ies'}.`);
        setEntries(prev => prev.filter(e => !selectedIds.includes(e._id)));
        setSelectedIds([]);
      } else {
        setDeleteError(data.message || 'Delete failed');
      }
    } catch {
      setDeleteError('Delete failed. Please try again.');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  // ── entry detail modal content ────────────────────────────
  const renderEntryContent = (entry) => {
    const mod = getModule(entry);
    const fields = mod?.fields || [];

    if (entry.entryType === 'single' && entry.entries?.length) {
      return (
        <div className="mlb-modal-entries">
          {entry.entries.map((e, i) => (
            <div key={e.entryId || i} className="mlb-sub-entry">
              <div className="mlb-sub-entry-header">
                <span>Entry #{i + 1}</span>
                <span className="mlb-sub-date">{formatDate(e.submittedAt)}</span>
              </div>
              {fields.length > 0 ? (
                <div className="mlb-fields-grid">
                  {fields.map(f => {
                    const val = e.data?.[f.key];
                    const display = val !== undefined && val !== null
                      ? (f.type === 'date' ? formatDate(val) : String(val))
                      : '—';
                    return (
                      <div key={f.key} className="mlb-field-row">
                        <span className="mlb-field-label">{f.label}</span>
                        <span className="mlb-field-value">{display}</span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                // Fallback: show raw key-value pairs from data
                <div className="mlb-fields-grid">
                  {Object.entries(e.data || {}).map(([k, v]) => (
                    <div key={k} className="mlb-field-row">
                      <span className="mlb-field-label">{k}</span>
                      <span className="mlb-field-value">{String(v)}</span>
                    </div>
                  ))}
                </div>
              )}
              {e.attachments?.length > 0 && (
                <p className="mlb-attachments-note">
                  {e.attachments.length} attachment{e.attachments.length > 1 ? 's' : ''}: {e.attachments.map(a => a.name).join(', ')}
                </p>
              )}
            </div>
          ))}
        </div>
      );
    }

    if (entry.entryType === 'bulk' && entry.counters) {
      const counters = entry.counters instanceof Map
        ? Object.fromEntries(entry.counters)
        : entry.counters;
      return (
        <div className="mlb-counters-grid">
          {fields.length > 0 ? (
            fields.map(f => {
              const val = counters[f.key] ?? 0;
              return (
                <div key={f.key} className="mlb-counter-card">
                  <span className="mlb-counter-label">{f.label}</span>
                  <span className="mlb-counter-val">{val}</span>
                  {f.target && (
                    <>
                      <div className="mlb-counter-bar">
                        <div
                          className="mlb-counter-fill"
                          style={{
                            width: `${Math.min((val / f.target) * 100, 100)}%`,
                            background: val >= f.target ? '#27ae60' : '#f39c12',
                          }}
                        />
                      </div>
                      <span className="mlb-counter-target">Target: {f.target}</span>
                    </>
                  )}
                </div>
              );
            })
          ) : (
            Object.entries(counters).map(([k, v]) => (
              <div key={k} className="mlb-counter-card">
                <span className="mlb-counter-label">{k}</span>
                <span className="mlb-counter-val">{v}</span>
              </div>
            ))
          )}
        </div>
      );
    }

    return <p className="mlb-no-data">No entry data available.</p>;
  };

  // ── JSX ───────────────────────────────────────────────────
  return (
    <div className="manage-logbooks-container">
      {/* Header */}
      <div className="mlb-header">
        <div className="mlb-header-content">
          <h2><FaBookMedical /> Manage Logbook Entries</h2>
          <p>Search for a student or teacher and view / delete their logbook entries</p>
        </div>
      </div>

      <div className="mlb-body">
        {/* Search Panel */}
        {!selectedUser && (
          <div className="mlb-search-panel">
            <div className="mlb-role-tabs">
              <button
                className={`mlb-role-tab ${roleFilter === 'student' ? 'active' : ''}`}
                onClick={() => { setRoleFilter('student'); setSearchResults([]); setSearchError(''); }}
              >
                <FaUserGraduate /> Students
              </button>
              <button
                className={`mlb-role-tab ${roleFilter === 'teacher' ? 'active' : ''}`}
                onClick={() => { setRoleFilter('teacher'); setSearchResults([]); setSearchError(''); }}
              >
                <FaChalkboardTeacher /> Teachers
              </button>
            </div>

            <form className="mlb-search-form" onSubmit={handleSearch}>
              <div className="mlb-search-input-wrap">
                <FaSearch className="mlb-search-icon" />
                <input
                  type="text"
                  placeholder={`Search ${roleFilter === 'student' ? 'students' : 'teachers'} by name, email or reg. number…`}
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="mlb-search-input"
                />
                {searchQuery && (
                  <button type="button" className="mlb-clear-btn" onClick={() => { setSearchQuery(''); setSearchResults([]); setSearchError(''); }}>
                    <FaTimes />
                  </button>
                )}
              </div>
              <button type="submit" className="mlb-search-btn" disabled={searchLoading}>
                {searchLoading ? 'Searching…' : 'Search'}
              </button>
            </form>

            {searchError && <p className="mlb-error">{searchError}</p>}

            {searchResults.length > 0 && (
              <div className="mlb-user-list">
                {searchResults.map(user => (
                  <div key={user._id} className="mlb-user-card" onClick={() => loadEntries(user)}>
                    <div className="mlb-user-avatar">
                      {user.role === 'student' ? <FaUserGraduate /> : <FaChalkboardTeacher />}
                    </div>
                    <div className="mlb-user-info">
                      <span className="mlb-user-name">{userName(user)}</span>
                      <span className="mlb-user-email">{user.email}</span>
                      {user.profile?.registrationNumber && (
                        <span className="mlb-user-reg">#{user.profile.registrationNumber}</span>
                      )}
                    </div>
                    <span className="mlb-view-label">View Entries →</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Entries Panel */}
        {selectedUser && (
          <div className="mlb-entries-panel">
            <div className="mlb-entries-header">
              <button className="mlb-back-btn" onClick={() => {
                setSelectedUser(null); setEntries([]); setSelectedIds([]);
                setDeleteError(''); setDeleteSuccess(''); setCourseMap({});
              }}>
                <FaChevronLeft /> Back
              </button>
              <div className="mlb-entries-title">
                <span className="mlb-entries-for">Entries for</span>
                <strong>{userName(selectedUser)}</strong>
                <span className="mlb-entries-email">{selectedUser.email}</span>
              </div>
              {entries.length > 0 && (
                <div className="mlb-bulk-actions">
                  <span className="mlb-selected-count">{selectedIds.length} selected</span>
                  <button
                    className="mlb-delete-btn"
                    disabled={selectedIds.length === 0 || deleting}
                    onClick={() => setConfirmDelete(true)}
                  >
                    <FaTrashAlt /> Delete Selected
                  </button>
                </div>
              )}
            </div>

            {deleteError && <p className="mlb-error">{deleteError}</p>}
            {deleteSuccess && <p className="mlb-success">{deleteSuccess}</p>}

            {entriesLoading && (
              <div className="mlb-loading">
                <div className="mlb-spinner" />
                <p>Loading entries…</p>
              </div>
            )}

            {entriesError && !entriesLoading && <p className="mlb-empty">{entriesError}</p>}

            {!entriesLoading && entries.length > 0 && (
              <div className="mlb-table-wrap">
                <table className="mlb-table">
                  <thead>
                    <tr>
                      <th>
                        <button className="mlb-checkbox-btn" onClick={toggleSelectAll}>
                          {selectedIds.length === entries.length
                            ? <FaCheckSquare className="mlb-chk checked" />
                            : <FaSquare className="mlb-chk" />}
                        </button>
                      </th>
                      <th>Module</th>
                      <th>Course</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map(entry => (
                      <tr key={entry._id} className={selectedIds.includes(entry._id) ? 'mlb-row-selected' : ''}>
                        <td>
                          <button className="mlb-checkbox-btn" onClick={() => toggleSelect(entry._id)}>
                            {selectedIds.includes(entry._id)
                              ? <FaCheckSquare className="mlb-chk checked" />
                              : <FaSquare className="mlb-chk" />}
                          </button>
                        </td>
                        <td className="mlb-td-module">{getModuleName(entry)}</td>
                        <td>{entry.courseId?.name || '—'}</td>
                        <td>
                          <span className={`mlb-type-badge mlb-type-${entry.entryType}`}>
                            {entry.entryType === 'single' ? 'Entries' : 'Counters'}
                          </span>
                        </td>
                        <td>
                          <span
                            className="mlb-status-badge"
                            style={{ background: STATUS_COLORS[entry.status] || '#95a5a6' }}
                          >
                            {STATUS_LABELS[entry.status] || entry.status}
                          </span>
                        </td>
                        <td>{formatDate(entry.metadata?.submittedAt || entry.createdAt)}</td>
                        <td className="mlb-actions-cell">
                          <button
                            className="mlb-view-btn"
                            title="View entry details"
                            onClick={() => setViewEntry(entry)}
                          >
                            <FaEye />
                          </button>
                          <button
                            className="mlb-single-delete-btn"
                            title="Delete this entry"
                            onClick={() => { setSelectedIds([entry._id]); setConfirmDelete(true); }}
                          >
                            <FaTrashAlt />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Entry Detail Modal ── */}
      {viewEntry && (
        <div className="mlb-overlay" onClick={() => setViewEntry(null)}>
          <div className="mlb-detail-modal" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="mlb-modal-header">
              <div className="mlb-modal-title-wrap">
                <FaBookMedical className="mlb-modal-icon" />
                <div>
                  <h3 className="mlb-modal-title">{getModuleName(viewEntry)}</h3>
                  <span className="mlb-modal-course">{viewEntry.courseId?.name || '—'}</span>
                </div>
              </div>
              <button className="mlb-modal-close" onClick={() => setViewEntry(null)}>
                <FaTimes />
              </button>
            </div>

            {/* Meta row */}
            <div className="mlb-modal-meta">
              <div className="mlb-meta-item">
                <span className="mlb-meta-label">Type</span>
                <span className={`mlb-type-badge mlb-type-${viewEntry.entryType}`}>
                  {viewEntry.entryType === 'single' ? 'Entries' : 'Counters'}
                </span>
              </div>
              <div className="mlb-meta-item">
                <span className="mlb-meta-label">Status</span>
                <span className="mlb-status-badge" style={{ background: STATUS_COLORS[viewEntry.status] || '#95a5a6' }}>
                  {STATUS_LABELS[viewEntry.status] || viewEntry.status}
                </span>
              </div>
              <div className="mlb-meta-item">
                <span className="mlb-meta-label">Submitted</span>
                <span>{formatDate(viewEntry.metadata?.submittedAt || viewEntry.createdAt)}</span>
              </div>
            </div>

            <div className="mlb-modal-body">
              {/* Entry data */}
              <div className="mlb-modal-section">
                <h4 className="mlb-modal-section-title">
                  <FaListAlt /> Entry Data
                </h4>
                {renderEntryContent(viewEntry)}
              </div>

              {/* Student notes */}
              {viewEntry.studentNotes && (
                <div className="mlb-modal-section">
                  <h4 className="mlb-modal-section-title"><FaStickyNote /> Student Notes</h4>
                  <p className="mlb-notes-text">{viewEntry.studentNotes}</p>
                </div>
              )}

              {/* Review */}
              {viewEntry.review?.reviewerId && (
                <div className="mlb-modal-section">
                  <h4 className="mlb-modal-section-title"><FaChalkboard /> Teacher Review</h4>
                  <div className="mlb-review-grid">
                    <div className="mlb-field-row">
                      <span className="mlb-field-label">Reviewer</span>
                      <span className="mlb-field-value">
                        {viewEntry.review.reviewerName ||
                          `${viewEntry.review.reviewerId?.profile?.firstName || ''} ${viewEntry.review.reviewerId?.profile?.lastName || ''}`.trim() ||
                          '—'}
                      </span>
                    </div>
                    {viewEntry.review.attemptActivity && (
                      <div className="mlb-field-row">
                        <span className="mlb-field-label">Attempt</span>
                        <span className="mlb-field-value">{ATTEMPT_LABELS[viewEntry.review.attemptActivity] || viewEntry.review.attemptActivity}</span>
                      </div>
                    )}
                    {viewEntry.review.rating && (
                      <div className="mlb-field-row">
                        <span className="mlb-field-label">Rating</span>
                        <span className="mlb-field-value">{RATING_LABELS[viewEntry.review.rating] || viewEntry.review.rating}</span>
                      </div>
                    )}
                    {viewEntry.review.facultyDecision && (
                      <div className="mlb-field-row">
                        <span className="mlb-field-label">Decision</span>
                        <span className="mlb-field-value">{DECISION_LABELS[viewEntry.review.facultyDecision] || viewEntry.review.facultyDecision}</span>
                      </div>
                    )}
                    {viewEntry.review.score !== undefined && (
                      <div className="mlb-field-row">
                        <span className="mlb-field-label">Score</span>
                        <span className="mlb-field-value">{viewEntry.review.score} / {viewEntry.review.maxScore || 100}</span>
                      </div>
                    )}
                    {viewEntry.review.feedback && (
                      <div className="mlb-field-row mlb-field-full">
                        <span className="mlb-field-label">Feedback</span>
                        <span className="mlb-field-value">{viewEntry.review.feedback}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="mlb-modal-footer">
              <button className="mlb-cancel-btn" onClick={() => setViewEntry(null)}>Close</button>
              <button
                className="mlb-confirm-delete-btn"
                onClick={() => { setSelectedIds([viewEntry._id]); setViewEntry(null); setConfirmDelete(true); }}
              >
                <FaTrashAlt /> Delete This Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Dialog ── */}
      {confirmDelete && (
        <div className="mlb-overlay" onClick={() => !deleting && setConfirmDelete(false)}>
          <div className="mlb-confirm-dialog" onClick={e => e.stopPropagation()}>
            <h3>Confirm Delete</h3>
            <p>
              Are you sure you want to permanently delete{' '}
              <strong>{selectedIds.length} entr{selectedIds.length === 1 ? 'y' : 'ies'}</strong>?
              This action cannot be undone and will also remove any attached files.
            </p>
            <div className="mlb-confirm-actions">
              <button className="mlb-cancel-btn" onClick={() => setConfirmDelete(false)} disabled={deleting}>
                Cancel
              </button>
              <button className="mlb-confirm-delete-btn" onClick={handleDeleteConfirm} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Yes, Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLogbooks;
